<template>
	<div
		class="group flex cursor-pointer flex-col gap-3 rounded-[var(--border-radius)] border border-dashed border-[var(--border)] bg-[var(--background-primary)] px-3 py-3 text-sm hover:border-[var(--accent-primary)] hover:bg-[var(--background-secondary)] focus:outline-2 focus:outline-offset-2 focus:outline-[var(--accent-primary)] md:flex-row md:items-center md:justify-between"
		role="button"
		tabindex="0"
		@click="emit('add')"
		@keydown.enter.prevent="emit('add')"
		@keydown.space.prevent="emit('add')"
	>
		<div class="flex min-w-0 flex-1 items-start gap-2">
			<div class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
				<img
					v-if="lenderOption?.icon"
					:src="`/logos/${lenderOption.icon}`"
					class="h-4 w-4"
				/>
				<AppIcon
					v-else
					name="account_balance"
					class="text-base text-[var(--text-secondary)]"
				/>
			</div>

			<div class="flex min-w-0 flex-1 flex-col gap-1">
				<div class="flex flex-wrap items-center gap-x-2 gap-y-1 leading-5">
					<span class="font-semibold text-[var(--text-primary)]">
						{{ lenderLabel }}
					</span>
					<div
						v-if="informatieAanvraag.content.mortgageNumber || formattedDate"
						class="inline-flex items-center gap-2 border-l border-current pl-2 text-xs text-[var(--text-secondary)]"
					>
						<span v-if="informatieAanvraag.content.mortgageNumber">
							{{ informatieAanvraag.content.mortgageNumber }}
						</span>
						<span
							v-if="informatieAanvraag.content.mortgageNumber && formattedDate"
						>
							|
						</span>
						<span v-if="formattedDate">
							{{ formattedDate }}
						</span>
					</div>
				</div>

				<div class="flex items-start gap-2 text-[var(--text-secondary)]">
					<AppIcon
						:name="status.icon"
						class="mt-0.5 text-base"
						:class="status.colorClass"
					/>
					<div class="min-w-0">
						<div class="font-medium text-[var(--text-primary)] leading-5">
							{{ status.displayName }}{{ status.statusCodeSuffix ?? "" }}
						</div>
						<p
							v-if="status.description"
							class="text-xs text-[var(--text-secondary)] leading-4"
						>
							{{ status.description }}
						</p>
					</div>
				</div>
			</div>
		</div>

		<div
			class="flex items-center gap-2 self-start text-[var(--text-secondary)] group-hover:text-[var(--accent-primary)] md:ml-4 md:self-center"
		>
			<AppIcon name="add" class="text-base" />
			<span class="font-medium whitespace-nowrap"> Klik om toe te voegen </span>
			<AppIcon name="chevron_right" class="text-lg" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { useProviderLenders } from "@/composables/useProviderLenders";
import { splitHdnValue } from "@/helpers/hdnHelpers";
import {
	getInformatieAanvraagStatus,
	type InformatieAanvraagWithDossier,
} from "./informatie-aanvraag-status";
import { DateTime } from "luxon";
import { computed } from "vue";

const props = defineProps<{
	informatieAanvraag: InformatieAanvraagWithDossier;
}>();

const emit = defineEmits<{
	(e: "add"): void;
}>();

const { getLenderOption } = useProviderLenders();

const lenderOption = computed(() =>
	getLenderOption(props.informatieAanvraag.content.lenderCode),
);

const lenderLabel = computed(() => {
	return (
		lenderOption.value?.label ||
		splitHdnValue(props.informatieAanvraag.content.lenderCode)[1] ||
		props.informatieAanvraag.content.lenderCode
	);
});

const formattedDate = computed(() => {
	if (!props.informatieAanvraag.createdAt) {
		return undefined;
	}

	return DateTime.fromMillis(props.informatieAanvraag.createdAt).toFormat(
		"dd-LL-yyyy",
	);
});

const status = computed(() =>
	getInformatieAanvraagStatus(
		props.informatieAanvraag.hdnDossier?.status ?? "sent",
		props.informatieAanvraag.hdnDossier?.statusMessage ?? undefined,
	),
);
</script>
